import {
  EventActions,
  EventActionTypes,
  EventType,
  EventTypes,
  LevelCompletedEvent,
  LevelPlayEvent,
  LevelPlayingEvent,
  LevelStartedEvent,
  RepertoirePlayingEvent,
  TierCompletedEvent,
  TierSessionFinishedEvent,
  TierStartedEvent,
  ExercisePlaythroughCompletedEvent,
  FlushEventsAndWrapUp,
} from 'Types/EventTypes';
import { put, select, takeEvery } from 'redux-saga/effects';
import axios from 'axios';
import * as eventActions from 'Actions/events';
import * as lessonActions from 'Actions/lesson';
import logger from 'Utils/Logger';
import axiosRetry from 'axios-retry';
import { eventQueue } from 'Models/EventQueue';
import { store } from 'Reducers';
import { v4 } from 'uuid';
import { getPlatformInfo, getDeviceClass, getViewport } from 'Actions/events';

axiosRetry(axios, { retryDelay: axiosRetry.linearDelay(), retries: 3 });
const baseUrl = `${process.env.REACT_APP_BACKEND_URL}/api/v1/`;
const eventMaxBatchSize = process.env.REACT_APP_EVENT_MAX_BATCH_SIZE;
if (!eventMaxBatchSize) {
  throw Error('REACT_APP_EVENT_MAX_BATCH_SIZE is not set');
}

const _eventMaxBatchSizeInt = parseInt(eventMaxBatchSize);

function* onStartup() {
  // Store in Redux
  const sessionId = v4();
  yield put(eventActions.setSessionId(sessionId));
  yield put(eventActions.setSessionIdComplete(sessionId));
}
type BaseEventType = {
  datetime: string;
  analytics_session_id: string;
  platform?: string;
  device_class?: string;
  viewport_width?: number;
  viewport_height?: number;
  // MUS-418: per-event idempotency key, stamped once at enqueue (see pushToQueue).
  client_event_id?: string;
};

type PlaySessionEventType = BaseEventType & { play_session_id?: string };

type PlaySessionEvents = PlaySessionEventType &
  (
    | LevelCompletedEvent
    | LevelPlayEvent
    | LevelPlayingEvent
    | RepertoirePlayingEvent
    | TierCompletedEvent
    | TierSessionFinishedEvent
    | TierStartedEvent
    | LevelStartedEvent
    | ExercisePlaythroughCompletedEvent
  );

type GenericEventType = (BaseEventType & EventTypes) | PlaySessionEvents;

function* pushToQueue(action: EventActions): Generator<unknown, void, unknown> {
  const _eventsUrl = baseUrl + 'events';
  const { sessionId } = yield select(({ eventReducer }) => eventReducer);
  const _state = yield select((state: unknown) => state);

  const baseEvent = {
    datetime: new Date().toISOString(),
    analytics_session_id: sessionId,
    platform: getPlatformInfo(),
    // MUS-500: device form-factor + current viewport on every event (the backend
    // persists them on the play/lifecycle tables and ignores them elsewhere).
    device_class: getDeviceClass(),
    ...getViewport(),
    // MUS-418: stamp a stable idempotency key ONCE, here at enqueue. The batch POST
    // is wrapped in axiosRetry (retries on network error), and a retry re-sends the
    // same queued payloads - so carrying the same client_event_id per event lets the
    // backend dedup the redelivery instead of writing duplicate rows. It must be set
    // at enqueue (not at flush) so every retry of a given event shares one id.
    client_event_id: v4(),
  };
  const payload: GenericEventType = {
    ...action.payload,
    ...baseEvent,
  };
  // Trace repertoire accuracy through the saga to catch null/NaN before it hits the backend
  if (
    action.payload.event_type === EventType.RepertoirePlayingEvent ||
    action.payload.event_type === EventType.RepertoireCompletedEvent
  ) {
    const accPayload = action.payload as {
      accuracy?: number;
      completion?: number;
      play_time?: number;
      event_type: string;
    };
    logger.info('[ACC-TRACE] SAGA pushToQueue repertoire event', {
      event_type: accPayload.event_type,
      accuracy: accPayload.accuracy,
      typeof_accuracy: typeof accPayload.accuracy,
      isNaN_accuracy:
        typeof accPayload.accuracy === 'number' ? isNaN(accPayload.accuracy) : 'not-a-number-type',
      completion: accPayload.completion,
      play_time: accPayload.play_time,
    });
  }
  logger.debug('about to add event to queue:', payload);
  try {
    // yield put(eventActions.setEventQeue(queue.concat([payload])))
    eventQueue.pushEvent(payload);
  } catch (error) {
    console.error(error);
  }
}

function* dispatchEvents(): Generator<unknown, void, unknown> {
  eventQueue.dispatchEvents();
  yield put(eventActions.dispatchEventsSuccess());
}

export function* flushEventsAndWrapUp(
  action: FlushEventsAndWrapUp,
): Generator<unknown, void, unknown> {
  yield put({ type: 'FLUSH_EVENTS_AND_WRAP_UP_STARTED' });
  logger.info('[flushEventsAndWrapUp] flushing event queue and scheduling wrap-up');
  // MUS-651: the wrap-up asked for is the won tier's session's, so a flush that
  // answers late cannot reopen it over a wrap-up already open or a later session.
  const playSessionId = action?.payload?.playSessionId;
  eventQueue.dispatchEvents(() => {
    logger.info('[flushEventsAndWrapUp] flush complete, dispatching initiateWrapUp');
    store.dispatch(lessonActions.initiateWrapUp(playSessionId));
  });
}

export default [
  onStartup(),
  takeEvery(EventActionTypes.DISPATCH_EVENTS, dispatchEvents),
  takeEvery(EventActionTypes.VISIT_EVENT_ACTION, pushToQueue),
  takeEvery(EventActionTypes.PAGE_VIEW_EVENT_ACTION, pushToQueue),
  takeEvery(EventActionTypes.WELCOME_LETTER_EVENT_ACTION, pushToQueue),
  takeEvery(EventActionTypes.INPUT_CHANGED_EVENT_ACTION, pushToQueue),
  takeEvery(EventActionTypes.SIGN_IN_EVENT_ACTION, pushToQueue),
  takeEvery(EventActionTypes.MIDI_CONNECTION_SUCCESS_EVENT_ACTION, pushToQueue),
  takeEvery(EventActionTypes.MIDI_CONNECTION_FAILURE_EVENT_ACTION, pushToQueue),
  takeEvery(EventActionTypes.LEVEL_PLAYING_EVENT_ACTION, pushToQueue),
  takeEvery(EventActionTypes.LEVEL_PLAY_EVENT_ACTION, pushToQueue),
  takeEvery(EventActionTypes.LEVEL_PAUSE_EVENT_ACTION, pushToQueue),
  takeEvery(EventActionTypes.LEVEL_STARTED_EVENT_ACTION, pushToQueue),
  takeEvery(EventActionTypes.LEVEL_COMPLETED_EVENT_ACTION, pushToQueue),
  takeEvery(EventActionTypes.TIER_STARTED_EVENT_ACTION, pushToQueue),
  takeEvery(EventActionTypes.TIER_COMPLETED_EVENT_ACTION, pushToQueue),
  takeEvery(EventActionTypes.TIER_SESSION_FINISHED_EVENT_ACTION, pushToQueue),
  takeEvery(EventActionTypes.REPERTOIRE_PLAYING_EVENT_ACTION, pushToQueue),
  takeEvery(EventActionTypes.REPERTOIRE_COMPLETED_EVENT_ACTION, pushToQueue),
  takeEvery(EventActionTypes.REPERTOIRE_STARTED_EVENT_ACTION, pushToQueue),
  takeEvery(EventActionTypes.REPERTOIRE_PAUSE_EVENT_ACTION, pushToQueue),
  takeEvery(EventActionTypes.REPERTOIRE_SECTION_PERFECTED_EVENT_ACTION, pushToQueue),
  takeEvery(EventActionTypes.REPERTOIRE_PLAY_EVENT_ACTION, pushToQueue),
  takeEvery(EventActionTypes.TUTORIAL_CHAPTER_STARTED_EVENT_ACTION, pushToQueue),
  takeEvery(EventActionTypes.TUTORIAL_CHAPTER_COMPLETED_EVENT_ACTION, pushToQueue),
  takeEvery(EventActionTypes.TUTORIAL_SKIPPED_EVENT_ACTION, pushToQueue),
  takeEvery(EventActionTypes.MOBILE_BLOCKED_EVENT_ACTION, pushToQueue),
  takeEvery(EventActionTypes.TUTORIAL_STARTED_EVENT_ACTION, pushToQueue),
  takeEvery(EventActionTypes.TUTORIAL_COMPLETED_EVENT_ACTION, pushToQueue),
  takeEvery(EventActionTypes.PHRASE_COMPLETED_EVENT_ACTION, pushToQueue),
  takeEvery(EventActionTypes.PHRASE_SERVED_EVENT_ACTION, pushToQueue),
  takeEvery(EventActionTypes.TEMPO_ADJUSTMENT_EVENT_ACTION, pushToQueue),
  takeEvery(EventActionTypes.CHEVRON_EVENT_ACTION, pushToQueue),
  takeEvery(EventActionTypes.SESSION_INTERRUPTION_EVENT_ACTION, pushToQueue),
  takeEvery(EventActionTypes.SAFARI_BLOCKED_EVENT_ACTION, pushToQueue),
  // TODO(MUS-278): temporary Firefox block while bugs are fixed.
  takeEvery(EventActionTypes.FIREFOX_BLOCKED_EVENT_ACTION, pushToQueue),
  takeEvery(EventActionTypes.REP_PHRASE_COMPLETED_EVENT_ACTION, pushToQueue),
  takeEvery(EventActionTypes.EXERCISE_PLAYTHROUGH_COMPLETED_EVENT_ACTION, pushToQueue),
  takeEvery(EventActionTypes.FLUSH_EVENTS_AND_WRAP_UP, flushEventsAndWrapUp),
];
